<template>
    <div class="job-content-status">
        <div class="status-container">
            <div class="status-badge" :class="getStatusClass(status)">
                <span class="status-label">{{ getStatusLabel(status) }}</span>
            </div>
        </div>
    </div>
</template>

<style lang="less" type="text/less">
.job-content-status {
    margin-bottom: 20px;
    
    .status-container {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        position: relative;

        &::before {
            content: "";
            position: absolute;
            top: 0px;
            left: 80%;
            width: 20%;
            height: 100%;
            --p: 10px;
            height: 40px;
            aspect-ratio: 1;
            clip-path: polygon(0 0, 100% 0, 100% calc(100% - var(--p)), 0 100%);
            background-color: #DE84F8;
        }
        
        // Background colors for status-container::before based on bg-color classes
        .status-badge.status-default ~ &::before,
        &:has(.status-badge.status-default)::before {
            background-color: #f9fafb;
        }

        .status-badge.bg-color-1 ~ &::before,
        &:has(.status-badge.bg-color-1)::before {
            background-color: #D9D9D9;
        }
        
        .status-badge.bg-color-2 ~ &::before,
        &:has(.status-badge.bg-color-2)::before {
            background-color: #DE84F8;
        }
        
        .status-badge.bg-color-3 ~ &::before,
        &:has(.status-badge.bg-color-3)::before {
            background-color: #F30011;
        }
        
        .status-badge.bg-color-4 ~ &::before,
        &:has(.status-badge.bg-color-4)::before {
            background-color: #AFE1F4;
        }
        
        .status-badge.bg-color-5 ~ &::before,
        &:has(.status-badge.bg-color-5)::before {
            background-color: #BCF3DB;
        }
        
        .status-badge.bg-color-6 ~ &::before,
        &:has(.status-badge.bg-color-6)::before {
            background-color: #065C43;
        }
    }
    
    .status-badge {
        position: relative;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        width: 100%;
        max-width: 100%;
        height: 30px;
        font-size: 14px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        color: white;
        background-color: #DE84F8;
        
        .status-label {
            position: absolute;
            top: 55%;
            margin: 0;
            text-align: center;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1;
        }
        
        &::after {
            content: "";
            position: absolute;
            top: 100%;
            left: 0;
            width: 80%;
            height: 20px;
            background: #DE84F8;
        }

        &::before {
            content: "";
            position: absolute;
            top: 100%;
            left: 80%;
            width: 0;
            height: 0;
            border-top: 20px solid #DE84F8;
            border-right: 20px solid transparent;
        }
        
        // Status color variations
        &.status-default {
            background-color: #f9fafb;
            color: #6b7280;

            &::after {
                background: #f9fafb;
            }
            &::before {
                border-top-color: #f9fafb;
            }
        }
        
        // Custom color classes with banner style
        &.bg-color-1 {
            background-color: #D9D9D9;
            color: #333333;
            
            &::after {
                background: #D9D9D9;
            }
            &::before {
                border-top-color: #D9D9D9;
            }
        }
        
        &.bg-color-2 {
            background-color: #DE84F8;
            color: #ffffff;
            
            &::after {
                background: #DE84F8;
            }
            &::before {
                border-top-color: #DE84F8;
            }
        }
        
        &.bg-color-3 {
            background-color: #F30011;
            color: #ffffff;
            
            &::after {
                background: #F30011;
            }
            &::before {
                border-top-color: #F30011;
            }
        }
        
        &.bg-color-4 {
            background-color: #AFE1F4;
            color: #333333;
            
            &::after {
                background: #AFE1F4;
            }
            &::before {
                border-top-color: #AFE1F4;
            }
        }
        
        &.bg-color-5 {
            background-color: #BCF3DB;
            color: #333333;
            
            &::after {
                background: #BCF3DB;
            }
            &::before {
                border-top-color: #BCF3DB;
            }
        }
        
        &.bg-color-6 {
            background-color: #065C43;
            color: #ffffff;
            
            &::after {
                background: #065C43;
            }
            &::before {
                border-top-color: #065C43;
            }
        }
        
        &.status-default {
            background-color: #f9fafb;
            color: #6b7280;
            
            &::after {
                background: #f9fafb;
            }
        }
    }
}
</style>

<script type="text/babel">
export default {
    name: 'JobContentStatus',
    
    props: {
        status: {
            type: String,
            default: null,
            validator: function (value) {
                // Allow null or one of the valid status values
                return value === null || [
                    'saved',
                    'submitted', 
                    'approved',
                    'submitted_media',
                    'approved_media',
                    'denied',
                    'denied_media'
                ].includes(value)
            }
        },
        user_type: {
            type: String,
            default: 'influencer',
            validator: function (value) {
                return ['influencer', 'brand'].includes(value)
            }
        }
    },
    
    methods: {
        getStatusLabel(status) {
            const influencerLabels = {
                'saved': 'Lav content',
                'submitted': 'Content afventer godkendelse af brand',
                'approved': 'Content godkendt, afventer post',
                'submitted_media': 'Content tilknyttet, afventer endelig godkendelse fra brandet',
                'approved_media': 'Content færdigt og leveret',
                'denied': 'Content afvist, se kommentarer',
                'denied_media': 'Brand har afvist posted content, forbind det rigtige opslag'
            }
            
            const brandLabels = {
                'saved': 'Afventer content fra influencer',
                'submitted': 'Gennemgå content',
                'approved': 'Content godkendt, mangler at blive postet',
                'submitted_media': 'Content postet, mangler endelig godkendelse',
                'approved_media': 'Content færdigt og leveret',
                'denied': 'Content afvist, afventer ændringer',
                'denied_media': 'Content afvist, afventer ændringer'
            }
            
            // Handle null status case
            if (status === null) {
                return this.user_type === 'brand' ? 'Afventer content fra influencer' : 'Lav content'
            }
            
            const labels = this.user_type === 'brand' ? brandLabels : influencerLabels
            return labels[status]
        },
        
        getStatusClass(status) {
            const influencerClasses = {
                'saved': 'bg-color-2',
                'submitted': 'bg-color-4',
                'approved': 'bg-color-5',
                'submitted_media': 'bg-color-6',
                'approved_media': 'bg-color-1',
                'denied': 'bg-color-3',
                'denied_media': 'bg-color-3'
            }
            
            const brandClasses = {
                'saved': 'bg-color-1',
                'submitted': 'bg-color-2',
                'approved': 'bg-color-5',
                'submitted_media': 'bg-color-6',
                'approved_media': 'bg-color-1',
                'denied': 'bg-color-3',
                'denied_media': 'bg-color-3'
            }
            
            // Handle null status case
            if (status === null) {
                return this.user_type === 'influencer' ? 'bg-color-2' : 'status-default'
            }
            
            const classes = this.user_type === 'brand' ? brandClasses : influencerClasses
            return classes[status] || 'status-default'
        }
    }
}
</script>
